import { useEffect, useRef, useState } from 'react'
import { ChevronDown, LogOut, Menu, ShieldCheck, UserRound, X } from 'lucide-react'
import { NotificationBell } from './NotificationBell'
import { useLogout, useMe, useProfile } from '../lib/auth'

// Buddhist-era Thai date for the account-created field.
function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })
  } catch {
    return iso
  }
}

// White navbar of the right column (CoreUI style): hamburger toggles the dark
// sidebar; bell + user avatar (popup menu: Profile / Logout) on the right. The
// avatar/identity come from the fast JWT-derived `me`; the Profile modal lazily
// pulls the full record from UMS (useProfile) once it is opened.
export function TopNav({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const { data: me } = useMe()
  const logout = useLogout()
  const [menuOpen, setMenuOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const { data: profile, isFetching: profileLoading } = useProfile(profileOpen)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [menuOpen])

  const name = me?.name ?? 'ผู้ใช้งาน'
  const initials =
    name
      .split(/\s+/)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U'
  const roleLabel = me?.isAdmin ? 'ผู้ดูแลระบบ (Admin)' : me?.roles?.[0] ?? 'ผู้ใช้งาน'
  // Modal prefers the full profile, falls back to the token-derived user while it loads.
  const prof = profile ?? me

  return (
    <header className="topnav">
      <button className="nav-burger" onClick={onToggleSidebar} aria-label="ย่อ/ขยายเมนู">
        <Menu size={19} />
      </button>

      <div className="topnav-right">
        <NotificationBell />

        <div className="avatar-wrap" ref={menuRef}>
          <button className="avatar-btn" onClick={() => setMenuOpen((v) => !v)} aria-label="บัญชีผู้ใช้">
            <span className="avatar">{initials}</span>
            <span className="avatar-name">{name}</span>
            <ChevronDown size={14} />
          </button>
          {menuOpen && (
            <div className="avatar-menu" role="menu">
              <div className="avatar-menu-head">
                <div className="avatar avatar-lg">{initials}</div>
                <div>
                  <div className="avatar-menu-name">{name}</div>
                  <div className="muted small">{me?.position ?? roleLabel}</div>
                </div>
              </div>
              <button role="menuitem" onClick={() => { setMenuOpen(false); setProfileOpen(true) }}>
                <UserRound size={15} /> Profile
              </button>
              <button role="menuitem" onClick={() => { setMenuOpen(false); logout.mutate() }} disabled={logout.isPending}>
                <LogOut size={15} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {profileOpen && (
        <div className="modal-backdrop" onClick={() => setProfileOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Profile</h3>
              <button className="icon-ghost" onClick={() => setProfileOpen(false)} aria-label="ปิด"><X size={16} /></button>
            </div>
            <dl className="kv">
              <dt>ชื่อ-สกุล</dt>
              <dd>{[prof?.title, prof?.name].filter(Boolean).join(' ') || '—'}</dd>

              <dt>Username</dt>
              <dd><code>{prof?.username ?? '—'}</code></dd>

              {profile?.employeeId && (<><dt>รหัสพนักงาน</dt><dd><code>{profile.employeeId}</code></dd></>)}

              {prof?.position && (
                <>
                  <dt>ตำแหน่ง</dt>
                  <dd>{prof.position}{profile?.positionLevel ? ` · ระดับ ${profile.positionLevel}` : ''}</dd>
                </>
              )}

              <dt>บทบาท</dt>
              <dd>
                {prof?.roles?.length ? (
                  <span className="role-chips">
                    {prof.isAdmin && <span className="role-chip admin"><ShieldCheck size={12} /> Admin</span>}
                    {prof.roles.filter((r) => !/admin/i.test(r)).map((r) => (
                      <span key={r} className="role-chip">{r}</span>
                    ))}
                  </span>
                ) : '—'}
              </dd>

              {prof?.orgPath && (<><dt>หน่วยงาน (org path)</dt><dd><code>{prof.orgPath}</code></dd></>)}

              {profile?.managerId && (<><dt>หัวหน้า (รหัส)</dt><dd><code>{profile.managerId}</code></dd></>)}

              {prof?.email && (
                <>
                  <dt>อีเมล</dt>
                  <dd>
                    {prof.email}
                    {profile?.emailVerified === false && <span className="muted small"> · ยังไม่ยืนยัน</span>}
                    {profile?.emailVerified === true && <span className="muted small"> · ✓ ยืนยันแล้ว</span>}
                  </dd>
                </>
              )}

              <dt>Realm / datasource</dt>
              <dd><code>{profile?.datasource ?? prof?.realm ?? '—'}</code></dd>

              {profile?.createdAt && (<><dt>สร้างบัญชีเมื่อ</dt><dd>{fmtDate(profile.createdAt)}</dd></>)}
            </dl>
            <p className="muted small" style={{ margin: '0.8rem 0 0' }}>
              {profileLoading
                ? 'กำลังดึงโปรไฟล์เต็มจาก UMS…'
                : 'เข้าสู่ระบบผ่าน UMS (ระบบยืนยันตัวตนพนักงาน ธ.ก.ส.) — ข้อมูลจาก token + GET /auth/users'}
            </p>
          </div>
        </div>
      )}
    </header>
  )
}
